// Fichier: /components/layout/PublicHeader.tsx (ou équivalent)
// Version avec logo central, style et taille restaurés, adapté à l'identité SIS.

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTenantContext } from '../../context/TenantContext';

// Icônes Lucide pertinentes pour SIS
import {
    Menu, X, ChevronDown, Info, Home, Wrench, Server, Code, Cloud, ShoppingCart, Headset, HardDrive,
    Shield, Zap, Award, Star, Phone, Mail, Clock, Facebook, Twitter, Instagram, Linkedin, Youtube, Search
} from 'lucide-react';
import { getProductCategories } from '../../services/shopService';

type SubMenuItem = {
    label: string;
    to: string;
    icon?: React.ComponentType<{ className?: string }>;
};

type MenuItem = {
    label: string;
    to?: string;
    icon?: React.ComponentType<{ className?: string }>;
    submenu?: SubMenuItem[];
};

// =========================================================================
// MENU ADAPTÉ POUR SOLUTION INFORMATIQUE SERVICES (SIS)
// =========================================================================
const NAV_ITEMS: MenuItem[] = [
    { label: 'Accueil', to: '/', icon: Home },
    {
        label: 'Nos Services',
        to: '#',
        icon: Wrench,
        submenu: [
            { label: 'Support & Maintenance', to: '#', icon: Wrench },
            { label: 'Réseaux & Infrastructure', to: '#', icon: Server },
            { label: 'Développement & Web', to: '#', icon: Code },
            { label: 'Services Cloud', to: '#', icon: Cloud },
        ]
    },
    { label: 'Boutique', to: '/boutique', icon: ShoppingCart },
    { label: 'À Propos', to: '/about', icon: Info },
];

const CTA_ITEM: MenuItem = { label: 'Contact', to: '/contact', icon: Headset };

// Mapping des icônes pour le Top Header
const iconMap: Record<string, React.ComponentType<{ className?: string; style?: React.CSSProperties }>> = {
    Home, Wrench, Server, Code, Cloud, ShoppingCart, Headset, HardDrive, Info, Shield, Zap, Award, Star,
    Phone, Mail, Clock, Facebook, Twitter, Instagram, Linkedin, Youtube,
};

// Hook pour déterminer le nombre d'items visibles (inchangé)
function useVisibleMenuItems(breakpoints = [1280, 1100, 900, 768]) {
    const [visibleCount, setVisibleCount] = useState(NAV_ITEMS.length);

    useEffect(() => {
        function handleResize() {
            const w = window.innerWidth;
            if (w >= breakpoints[0]) setVisibleCount(NAV_ITEMS.length);
            else if (w >= breakpoints[1]) setVisibleCount(4);
            else if (w >= breakpoints[2]) setVisibleCount(3);
            else if (w >= breakpoints[3]) setVisibleCount(2);
            else setVisibleCount(1);
        }
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [breakpoints]);

    return visibleCount;
}

export const PublicHeader: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isOverflowOpen, setIsOverflowOpen] = useState(false);
    const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
    const [submenuTimeout, setSubmenuTimeout] = useState<NodeJS.Timeout | null>(null);
    const [lastScrollY, setLastScrollY] = useState(0);
    const [headerVisible, setHeaderVisible] = useState(true);
    const [isScrolled, setIsScrolled] = useState(false);
    const [topbarDismissed, setTopbarDismissed] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const { tenant } = useTenantContext();
    const navigate = useNavigate();

    // Configuration du Top Header
    const topbarConfig = React.useMemo(() => {
        const base = tenant?.config?.publicHeader?.topbar || {};
        const defaults = {
            enabled: false,
            showOnMobile: false,
            size: 'compact',
            backgroundColor: '#0f172a',
            textColor: '#ffffff',
            iconColor: '#f97316',
            borderBottom: true,
            borderColor: '#e5e7eb',
            left: {
                showPhone: false,
                phone: '',
                showEmail: !!tenant?.email,
                email: tenant?.email || '',
                showHours: false,
                hours: 'Lun-Ven: 9h-18h',
            },
            message: {
                enabled: false,
                text: '',
                icon: 'Info',
                href: '',
                dismissible: true,
            },
            social: {
                enabled: false,
                items: [
                    { id: 'facebook', name: 'Facebook', icon: 'Facebook', url: '', enabled: false },
                    { id: 'twitter', name: 'Twitter', icon: 'Twitter', url: '', enabled: false },
                    { id: 'instagram', name: 'Instagram', icon: 'Instagram', url: '', enabled: false },
                    { id: 'linkedin', name: 'LinkedIn', icon: 'Linkedin', url: '', enabled: false },
                    { id: 'youtube', name: 'YouTube', icon: 'Youtube', url: '', enabled: false },
                ],
            },
        };
        return {
            ...defaults,
            ...base,
            left: { ...defaults.left, ...base.left },
            message: { ...defaults.message, ...base.message },
            social: {
                ...defaults.social,
                ...base.social,
                items: base.social?.items || defaults.social.items,
            },
        };
    }, [tenant]);

    const topbarVisibilityClass = topbarConfig?.showOnMobile ? 'block' : 'hidden lg:block';
    const topbarPadding = topbarConfig?.size === 'compact' ? 'py-1 text-xs' : 'py-2 text-sm';

    // Configuration du Bottom Header (bottombar) orienté boutique
    const bottombarConfig = useMemo(() => {
        const base = tenant?.config?.publicHeader?.bottombar || {};
        const defaults = {
            enabled: false,
            showOnMobile: true,
            backgroundColor: '#ffffff',
            textColor: '#1e1b4b',
            iconColor: '#f97316',
            borderTop: true,
            borderColor: '#e5e7eb',
            categories: {
                enabled: true,
                source: 'auto' as 'auto' | 'manual',
                pillStyle: 'filled' as 'filled' | 'outline',
                showCounts: false,
                maxItems: 8,
                manualItems: [
                    { id: 'promos', name: 'Promotions', href: '/boutique?categorie=promotions' },
                    { id: 'nouveautes', name: 'Nouveautés', href: '/boutique?categorie=nouveautes' },
                    { id: 'best', name: 'Meilleures Ventes', href: '/boutique?categorie=best' },
                ],
            },
            actions: {
                showSearch: true,
                searchPlaceholder: 'Rechercher dans la boutique...',
                showContact: true,
                contactHref: '/contact',
            },
        } as const;
        const merged = {
            ...defaults,
            ...base,
            categories: {
                ...defaults.categories,
                ...(base as any).categories,
                manualItems: (base as any)?.categories?.manualItems || defaults.categories.manualItems,
            },
            actions: {
                ...defaults.actions,
                ...(base as any).actions,
            },
        };
        return merged as typeof defaults & Record<string, any>;
    }, [tenant]);

    const bottombarVisibilityClass = bottombarConfig?.showOnMobile ? 'block' : 'hidden lg:block';
    const [bottomCategories, setBottomCategories] = useState<any[]>([]);
    const [bottomSearch, setBottomSearch] = useState('');

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOverflowOpen(false);
                setOpenSubmenu(null);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    
    // Effet pour gérer le comportement du header lors du défilement
    useEffect(() => {
        const controlHeader = () => {
            // Ne pas cacher le header si le menu mobile est ouvert
            if (isOpen) {
                setHeaderVisible(true);
                setIsScrolled(false);
                return;
            }
            
            // Seuil de défilement avant de cacher le header (en pixels)
            const scrollThreshold = 20;
            
            // Obtenir la position actuelle du défilement
            const currentScrollY = window.scrollY;
            
            // Déterminer si on défile vers le bas et si on a dépassé le seuil
            if (currentScrollY > lastScrollY && currentScrollY > scrollThreshold) {
                // Défilement vers le bas - cacher le header
                setHeaderVisible(false);
                setIsScrolled(true);
            } else {
                // Défilement vers le haut ou en haut de page - montrer le header
                setHeaderVisible(true);
                setIsScrolled(currentScrollY > 10);
            }
            
            // Mettre à jour la dernière position de défilement
            setLastScrollY(currentScrollY);
        };
        
        // Ajouter l'écouteur d'événement avec throttling pour de meilleures performances
        let timeoutId: NodeJS.Timeout | null = null;
        const handleScroll = () => {
            if (timeoutId) return;
            timeoutId = setTimeout(() => {
                controlHeader();
                timeoutId = null;
            }, 100);
        };
        
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [lastScrollY, isOpen]);

    // Charger les catégories pour le Bottom Header si source=auto
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                if (bottombarConfig?.enabled && bottombarConfig?.categories?.enabled && bottombarConfig?.categories?.source === 'auto') {
                    const cats = await getProductCategories();
                    if (!cancelled) setBottomCategories(Array.isArray(cats) ? cats : []);
                } else {
                    setBottomCategories([]);
                }
            } catch (e) {
                console.error('Erreur Bottom Header catégories:', e);
                if (!cancelled) setBottomCategories([]);
            }
        };
        load();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bottombarConfig?.enabled, bottombarConfig?.categories?.enabled, bottombarConfig?.categories?.source]);

    const effectiveBottomCategories = useMemo(() => {
        if (!bottombarConfig?.categories?.enabled) return [] as any[];
        const max = Number(bottombarConfig?.categories?.maxItems ?? 8);
        if (bottombarConfig?.categories?.source === 'manual') {
            const manual = Array.isArray(bottombarConfig?.categories?.manualItems) ? bottombarConfig.categories.manualItems : [];
            return manual.slice(0, max);
        }
        return (bottomCategories || []).slice(0, max);
    }, [bottomCategories, bottombarConfig]);

    // Ouvrir par défaut le premier sous-menu sur mobile lorsque le menu mobile s'ouvre
    useEffect(() => {
        if (isOpen && typeof window !== 'undefined' && window.innerWidth < 1024) { // 1024px ~ breakpoint lg
            const firstWithSubmenu = NAV_ITEMS.find(i => i.submenu && i.submenu.length > 0);
            if (firstWithSubmenu) {
                setOpenSubmenu(firstWithSubmenu.label);
            }
        }
    }, [isOpen]);

    const handleSubmenuClose = () => {
        const timeout = setTimeout(() => {
            setOpenSubmenu(null);
        }, 150);
        setSubmenuTimeout(timeout);
    };

    const renderMenuItem = (item: MenuItem, isMobile: boolean = false) => {
        if (item.submenu) {
            return (
                <div key={item.label} className="relative mb-0" onMouseLeave={isMobile ? undefined : handleSubmenuClose}>
                    <button 
                        onMouseEnter={() => {
                            if (submenuTimeout) clearTimeout(submenuTimeout);
                            !isMobile && setOpenSubmenu(item.label)
                        }}
                        onClick={() => {
                            if (isMobile) {
                                // Sur mobile, toggle le sous-menu
                                setOpenSubmenu(openSubmenu === item.label ? null : item.label)
                            } else {
                                // Sur desktop, ouvrir le sous-menu au clic
                                setOpenSubmenu(item.label)
                            }
                        }} 
                        className="relative flex items-center space-x-1 px-4 py-3 text-sm font-bold text-[#1e1b4b] hover:text-[#f97316] transition-all duration-300 group/btn w-full backdrop-blur-sm hover:bg-white/60 rounded-lg"
                    >
                        {item.icon && <item.icon className="w-4 h-4 mr-2 text-[#3b82f6] group-hover/btn:text-[#f97316] transition-colors duration-200" />}
                        <span className="relative">
                            {item.label}
                            <span className={`absolute -bottom-1 left-0 h-0.5 bg-[#f97316] transition-all duration-300 ${openSubmenu === item.label ? 'w-full' : 'w-0 group-hover/btn:w-full'}`} />
                        </span>
                        <ChevronDown className={`w-3 h-3 text-[#3b82f6] group-hover/btn:text-[#f97316] transition-all duration-200 ${openSubmenu === item.label ? 'rotate-180' : ''}`} />
                    </button>
                    
                    <div 
                        className={`${isMobile ? 'static' : 'absolute'} ${isMobile ? 'mt-0 mb-0' : 'top-full left-0 mt-2'} w-full md:w-60 bg-white rounded-lg shadow-lg border border-slate-200 overflow-hidden transition-all duration-200 z-50 ${openSubmenu === item.label ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'}`}
                        onMouseEnter={() => {
                             if (submenuTimeout) clearTimeout(submenuTimeout);
                            !isMobile && setOpenSubmenu(item.label)
                        }}
                        onMouseLeave={isMobile ? undefined : handleSubmenuClose}
                    >
                        <div className="py-1">
                            {item.submenu.map((subItem, index) => (
                                <Link 
                                    key={`${item.label}-${index}`} 
                                    to={subItem.to} 
                                    onClick={() => {
                                        isMobile && setIsOpen(false)
                                        setOpenSubmenu(null)
                                    }}
                                    className="flex items-center px-4 py-2 text-sm text-[#1e1b4b]/80 hover:text-[#1e1b4b] hover:bg-[#3b82f6]/5 transition-colors duration-150 group/sub"
                                >
                                    {subItem.icon && <subItem.icon className="w-4 h-4 mr-3 text-[#3b82f6]/60 group-hover/sub:text-[#f97316] transition-colors" />}
                                    <span>{subItem.label}</span>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <Link 
                key={item.to} 
                to={item.to!} 
                onClick={() => isMobile && setIsOpen(false)}
                className="relative flex items-center space-x-1 px-4 py-3 text-sm font-bold text-[#1e1b4b] hover:text-[#f97316] transition-all duration-300 group backdrop-blur-sm hover:bg-white/60 rounded-lg"
            >
                {item.icon && <item.icon className="w-4 h-4 mr-2 text-[#3b82f6] group-hover:text-[#f97316] transition-colors duration-200" />}
                <span className="relative">
                    {item.label}
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#f97316] group-hover:w-full transition-all duration-300" />
                </span>
            </Link>
        );
    };

    const visibleCount = useVisibleMenuItems();
    const visibleItems = NAV_ITEMS.slice(0, visibleCount);
    const overflowItems = NAV_ITEMS.slice(visibleCount);

    return (
        <header className={`sticky ${headerVisible ? 'top-0' : '-top-20'} z-40 bg-white/95 backdrop-blur-xl shadow-xl transition-all duration-500 ease-in-out ${isScrolled ? 'shadow-2xl bg-white/98' : ''}`}>
            {/* Topbar configurable */}
            {topbarConfig?.enabled && !topbarDismissed && (
                <div className={`relative z-20 ${topbarVisibilityClass} ${topbarPadding}`} style={{ backgroundColor: topbarConfig.backgroundColor, color: topbarConfig.textColor }}>
                    <div className="container mx-auto px-8 flex items-center justify-between">
                        {/* Left: contact */}
                        <div className="flex items-center space-x-4">
                            {topbarConfig?.left?.showPhone && topbarConfig?.left?.phone && (
                                <a href={`tel:${topbarConfig.left.phone}`} className="flex items-center space-x-1 hover:opacity-90 transition-opacity">
                                    <Phone className="w-3.5 h-3.5" style={{ color: topbarConfig.iconColor }} />
                                    <span>{topbarConfig.left.phone}</span>
                                </a>
                            )}
                            {topbarConfig?.left?.showEmail && topbarConfig?.left?.email && (
                                <a href={`mailto:${topbarConfig.left.email}`} className="flex items-center space-x-1 hover:opacity-90 transition-opacity">
                                    <Mail className="w-3.5 h-3.5" style={{ color: topbarConfig.iconColor }} />
                                    <span>{topbarConfig.left.email}</span>
                                </a>
                            )}
                            {topbarConfig?.left?.showHours && topbarConfig?.left?.hours && (
                                <div className="hidden md:flex items-center space-x-1">
                                    <Clock className="w-3.5 h-3.5" style={{ color: topbarConfig.iconColor }} />
                                    <span>{topbarConfig.left.hours}</span>
                                </div>
                            )}
                        </div>

                        {/* Center: message/announcement */}
                        <div className="flex-1 flex justify-center px-2">
                            {topbarConfig?.message?.enabled && topbarConfig?.message?.text && (
                                topbarConfig?.message?.href ? (
                                    topbarConfig.message.href.startsWith('/') ? (
                                        <Link to={topbarConfig.message.href} className="flex items-center space-x-2 hover:opacity-90 transition-opacity">
                                            {React.createElement(iconMap[topbarConfig.message.icon] || Info, { className: 'w-3.5 h-3.5', style: { color: topbarConfig.iconColor } })}
                                            <span className="truncate">{topbarConfig.message.text}</span>
                                        </Link>
                                    ) : (
                                        <a href={topbarConfig.message.href} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 hover:opacity-90 transition-opacity">
                                            {React.createElement(iconMap[topbarConfig.message.icon] || Info, { className: 'w-3.5 h-3.5', style: { color: topbarConfig.iconColor } })}
                                            <span className="truncate">{topbarConfig.message.text}</span>
                                        </a>
                                    )
                                ) : (
                                    <div className="flex items-center space-x-2">
                                        {React.createElement(iconMap[topbarConfig.message.icon] || Info, { className: 'w-3.5 h-3.5', style: { color: topbarConfig.iconColor } })}
                                        <span className="truncate">{topbarConfig.message.text}</span>
                                    </div>
                                )
                            )}
                        </div>

                        {/* Right: social icons and dismiss */}
                        <div className="flex items-center space-x-3">
                            {topbarConfig?.social?.enabled && Array.isArray(topbarConfig?.social?.items) && topbarConfig.social.items.filter((s: any) => s.enabled && s.url).map((s: any) => {
                                const Icon = iconMap[s.icon] || Star;
                                return (
                                    <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer" className="hover:opacity-90 transition-opacity">
                                        <Icon className="w-3.5 h-3.5" style={{ color: topbarConfig.iconColor }} />
                                    </a>
                                );
                            })}
                            {topbarConfig?.message?.dismissible && (
                                <button onClick={() => setTopbarDismissed(true)} className="p-1 rounded hover:bg-white/10 transition-colors" aria-label="Fermer l'annonce">
                                    <X className="w-3.5 h-3.5" style={{ color: topbarConfig.iconColor }} />
                                </button>
                            )}
                        </div>
                    </div>
                    {topbarConfig?.borderBottom && (
                        <div className="h-px w-full" style={{ backgroundColor: topbarConfig.borderColor, opacity: 0.35 }} />
                    )}
                </div>
            )}
            {/* Bordure bottom avec dégradé luxueux */}
            <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#f97316]/40 via-[#fbbf24]/60 to-[#f97316]/40 transition-all duration-500 ${isScrolled ? 'h-1.5 from-[#f97316]/60 via-[#fbbf24]/80 to-[#f97316]/60' : ''}`} />
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-white/80 to-transparent" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.98) 30%, rgba(255,255,255,0.95) 70%, rgba(241,245,249,0.98) 100%)' }} />
            <div className="absolute inset-0 bg-gradient-to-r from-[#f97316]/2 via-transparent to-[#1e1b4b]/2 opacity-50" />
            <div className="absolute top-3 left-24 w-2 h-2 bg-gradient-to-r from-[#f97316] to-[#fbbf24] rounded-full animate-pulse shadow-lg" />
            <div className="absolute top-5 right-36 w-2.5 h-2.5 bg-gradient-to-r from-[#1e1b4b] to-[#312e81] rounded-full animate-pulse shadow-lg" style={{ animationDelay: '1s' }} />
            <div className="absolute bottom-3 left-1/3 w-1.5 h-1.5 bg-gradient-to-r from-[#f97316] to-[#fbbf24] rounded-full animate-bounce shadow-md" />
            <div className="absolute top-1/2 left-1/4 w-1 h-1 bg-[#f97316]/60 rounded-full animate-ping" style={{ animationDelay: '2s' }} />
            <div className="absolute top-1/2 right-1/4 w-1 h-1 bg-[#1e1b4b]/60 rounded-full animate-ping" style={{ animationDelay: '3s' }} />
            
            <nav className="container mx-auto px-8 py-3 relative z-10" ref={menuRef}>
                <div className="flex justify-between items-center h-10">
                    {/* Menu Desktop Gauche */}
                    <div className="hidden lg:flex items-center justify-start flex-1">
                        <div className="flex items-center space-x-8">
                            {visibleItems.slice(0, Math.ceil(visibleItems.length / 2)).map(item => renderMenuItem(item))}
                        </div>
                    </div>
                    
                    {/* Logo au centre sur desktop, à gauche sur mobile */}
                    <div className="flex-shrink-0 relative z-50 lg:absolute lg:left-1/2 lg:transform lg:-translate-x-1/2">
                        <Link to="/" className="group flex items-center">
                           <div className={`relative h-12 w-20 md:w-32 lg:w-40 flex items-center justify-center overflow-visible transition-all duration-300 ${isScrolled ? 'lg:w-20' : ''}`}>
                                <div className={`absolute top-0 lg:-top-1 left-3/4 md:left-1/2 transform -translate-x-1/2 w-20 h-20 md:w-28 md:h-28 lg:w-32 lg:h-32 rounded-xl bg-white border border-white/30 z-40 transition-all duration-500 ${isScrolled ? 'lg:w-16 lg:h-16' : ''}`}>
                                    <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-[#f97316]/5 via-transparent to-[#1e1b4b]/5 opacity-30"></div>
                                </div>
                                {tenant?.logo ? (
                                    <div className={`absolute top-0 md:top-2 lg:-top-1 left-3/4 md:left-1/2 transform -translate-x-1/2 w-20 h-20 md:w-28 md:h-28 lg:w-32 lg:h-32 rounded-xl overflow-hidden z-50 transition-all duration-700 group-hover:brightness-110 group-hover:scale-105 p-1 md:p-1.5 lg:p-2 ${isScrolled ? 'lg:w-16 lg:h-16 lg:p-1' : ''}`}>
                                        <img className="w-full h-full object-contain" src={tenant.logo} alt={`Logo ${tenant?.name || 'SIS'}`}
                                            style={{ filter: 'drop-shadow(0 4px 8px rgba(30, 27, 75, 0.2)) drop-shadow(0 2px 4px rgba(249, 115, 22, 0.15)) md:drop-shadow(0 6px 12px rgba(30, 27, 75, 0.25)) md:drop-shadow(0 3px 6px rgba(249, 115, 22, 0.18)) lg:drop-shadow(0 8px 16px rgba(30, 27, 75, 0.3)) lg:drop-shadow(0 4px 8px rgba(249, 115, 22, 0.2))' }}
                                        />
                                    </div>
                                ) : (
                                    <div className={`absolute top-0 md:top-2 lg:-top-1 left-3/4 md:left-1/2 transform -translate-x-1/2 w-20 h-20 md:w-28 md:h-28 lg:w-32 lg:h-32 rounded-xl overflow-hidden z-50 bg-gradient-to-br from-[#1e1b4b] via-[#312e81] to-[#1e1b4b] flex items-center justify-center p-1 md:p-1.5 lg:p-2 transition-all duration-700 group-hover:scale-105 ${isScrolled ? 'lg:w-16 lg:h-16 lg:p-1' : ''}`}>
                                        <HardDrive className={`h-16 w-16 md:h-20 md:w-20 text-white ${isScrolled ? 'lg:h-10 lg:w-10' : ''}`} />
                                    </div>
                                )}
                           </div>
                        </Link>
                    </div>

                    {/* Élément visuel entre le logo et le menu burger sur mobile */}
                    <div className="lg:hidden flex-1 flex justify-center items-center mx-2">
                        <div className="flex items-center justify-center space-x-2 bg-gradient-to-r from-[#1e1b4b]/5 via-[#f97316]/10 to-[#1e1b4b]/5 px-3 py-1.5 rounded-full">
                            <Shield className="w-5 h-5 text-[#1e1b4b] animate-pulse" style={{ animationDelay: '0s' }} />
                            <div className="hidden xs:flex items-center">
                                <span className="text-xs font-semibold bg-clip-text text-transparent bg-gradient-to-r from-[#1e1b4b] to-[#f97316]">Solutions Informatiques</span>
                            </div>
                            <Star className="w-4 h-4 text-[#f97316] animate-pulse" style={{ animationDelay: '0.5s' }} />
                        </div>
                    </div>

                    {/* Menu Desktop Droite */}
                    <div className="hidden lg:flex items-center justify-end flex-1">
                        <div className="flex items-center space-x-8">
                            {visibleItems.slice(Math.ceil(visibleItems.length / 2)).map(item => renderMenuItem(item))}
                            {/* Overflow menu si nécessaire, non affiché avec 4 items mais la logique est là */}
                        </div>
                    </div>

                    {/* Bouton Contact à droite */}
                    <div className="hidden lg:flex items-center justify-end flex-shrink-0 ml-6">
                         <Link
                           to={CTA_ITEM.to!}
                           className="bg-gradient-to-r from-[#1e1b4b] to-[#312e81] text-white border-2 border-[#f97316]/20 hover:from-[#f97316] hover:to-[#ea580c] hover:border-[#f97316] flex items-center space-x-2 px-6 py-3 text-sm font-bold rounded-xl transition-all duration-500 shadow-xl hover:shadow-2xl transform hover:scale-110 hover:-translate-y-0.5 backdrop-blur-sm"
                        >
                            {CTA_ITEM.icon && <CTA_ITEM.icon className="w-5 h-5 text-[#f97316]" />}
                            <span>{CTA_ITEM.label}</span>
                        </Link>
                    </div>

                    {/* Menu Burger pour mobile */}
                    <div className="lg:hidden">
                        <button 
                            onClick={() => {
                                setIsOpen(!isOpen);
                                // Assurer que le header est visible quand on ouvre le menu
                                if (!isOpen) setHeaderVisible(true);
                            }} 
                            aria-label="Ouvrir le menu" 
                            className="group p-2 text-[#1e1b4b] rounded-lg hover:bg-slate-100 transition-all duration-300 relative overflow-hidden"
                        >
                           <Menu className="w-7 h-7 text-[#1e1b4b] group-hover:text-[#f97316] group-hover:scale-110 transition-all duration-300 relative z-10" />
                        </button>
                    </div>
                </div>
            </nav>
            
            {/* Menu Mobile Déroulant */}
            {isOpen && (
                <div className="lg:hidden fixed top-0 inset-x-0 p-2 transition transform origin-top-right z-50">
                    <div className="rounded-lg shadow-lg bg-white/95 backdrop-blur-md ring-1 ring-black ring-opacity-5 overflow-hidden">
                        <div className="px-5 pt-4 flex items-center justify-between">
                            <Link to="/" onClick={() => setIsOpen(false)}>
                                {tenant?.logo ? (
                                    <img className="h-10 w-auto" src={tenant.logo} alt={`Logo ${tenant?.name || 'SIS'}`} />
                                ) : (
                                    <div className="h-10 w-10 bg-[#1e1b4b] rounded-lg flex items-center justify-center">
                                        <HardDrive className="w-6 h-6 text-white" />
                                    </div>
                                )}
                            </Link>
                            <button
                                onClick={() => {
                                    setIsOpen(false);
                                    // Réinitialiser la visibilité du header en fonction du défilement actuel
                                    setHeaderVisible(window.scrollY <= 20);
                                }}
                                className="p-2 rounded-md hover:bg-slate-100 transition-colors"
                                aria-label="Fermer le menu"
                            >
                                <X className="w-6 h-6 text-slate-700" />
                            </button>
                        </div>
                        <div className="px-2 pt-2 pb-3 space-y-0">
                            {NAV_ITEMS.map(item => renderMenuItem(item, true))}
                            
                            <div className="pt-1 px-2">
                                <Link 
                                    to={CTA_ITEM.to!}
                                    onClick={() => setIsOpen(false)}
                                    className="bg-[#1e1b4b] text-white hover:bg-[#f97316] flex items-center justify-center space-x-2 w-full px-4 py-3 text-base font-bold rounded-lg transition-colors duration-300 shadow-md"
                                >
                                    {CTA_ITEM.icon && <CTA_ITEM.icon className="w-5 h-5 text-[#f97316]" />}
                                    <span>{CTA_ITEM.label}</span>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Bottom Header (barre boutique) */}
            {bottombarConfig?.enabled && (
                <div className={`relative z-10 ${bottombarVisibilityClass}`} style={{ backgroundColor: bottombarConfig.backgroundColor, color: bottombarConfig.textColor }}>
                    {bottombarConfig?.borderTop && (
                        <div className="h-px w-full" style={{ backgroundColor: bottombarConfig.borderColor, opacity: 0.35 }} />
                    )}
                    <div className="container mx-auto px-8 py-2 flex items-center gap-4">
                        {/* Liste des catégories (pills) */}
                        <div className="flex-1 overflow-x-auto">
                            {bottombarConfig?.categories?.enabled && (
                                <div className="flex items-center gap-2 whitespace-nowrap pr-2">
                                    {effectiveBottomCategories.map((cat: any) => {
                                        const pillBase = 'inline-flex items-center px-3 py-1 text-xs font-medium rounded-full transition-colors duration-200';
                                        const isOutline = (bottombarConfig?.categories?.pillStyle === 'outline');
                                        const styleFilled: React.CSSProperties = {
                                            backgroundColor: bottombarConfig.iconColor,
                                            color: '#ffffff',
                                            borderColor: bottombarConfig.iconColor,
                                        };
                                        const styleOutline: React.CSSProperties = {
                                            backgroundColor: 'transparent',
                                            color: bottombarConfig.iconColor,
                                            borderColor: bottombarConfig.iconColor,
                                        };
                                        const content = <span>{cat.name}</span>;
                                        const className = `${pillBase} border hover:opacity-90`;
                                        if (cat.href) {
                                            return (
                                                <Link key={cat.id || cat.slug} to={cat.href} className={className} style={isOutline ? styleOutline : styleFilled}>
                                                    {content}
                                                </Link>
                                            );
                                        }
                                        const to = cat.slug ? `/boutique?categorie=${encodeURIComponent(cat.slug)}` : '/boutique';
                                        return (
                                            <Link key={cat.id || cat.slug} to={to} className={className} style={isOutline ? styleOutline : styleFilled}>
                                                {content}
                                            </Link>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Actions: Recherche + Contact */}
                        <div className="flex items-center gap-3">
                            {bottombarConfig?.actions?.showSearch && (
                                <form
                                    onSubmit={(e) => { e.preventDefault(); navigate(`/boutique?q=${encodeURIComponent(bottomSearch)}`); }}
                                >
                                    <div className="flex items-center bg-white/10 border border-white/10 rounded-md px-2 py-1">
                                        <Search className="w-4 h-4 mr-1" style={{ color: bottombarConfig.iconColor }} />
                                        <input
                                            type="text"
                                            value={bottomSearch}
                                            onChange={(e) => setBottomSearch(e.target.value)}
                                            placeholder={bottombarConfig?.actions?.searchPlaceholder || 'Rechercher...'}
                                            className="bg-transparent outline-none placeholder-black/40 text-xs md:text-sm w-32 md:w-56"
                                            style={{ color: bottombarConfig.textColor }}
                                        />
                                    </div>
                                </form>
                            )}
                            {bottombarConfig?.actions?.showContact && (
                                bottombarConfig.actions.contactHref?.startsWith('/') ? (
                                    <Link to={bottombarConfig.actions.contactHref} className="flex items-center text-xs md:text-sm hover:opacity-90">
                                        <Headset className="w-4 h-4 mr-1" style={{ color: bottombarConfig.iconColor }} />
                                        <span>Contact</span>
                                    </Link>
                                ) : (
                                    <a href={bottombarConfig.actions.contactHref} target="_blank" rel="noopener noreferrer" className="flex items-center text-xs md:text-sm hover:opacity-90">
                                        <Headset className="w-4 h-4 mr-1" style={{ color: bottombarConfig.iconColor }} />
                                        <span>Contact</span>
                                    </a>
                                )
                            )}
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
};

export default PublicHeader;